function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo } from 'react';
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import { useStore, useStoreActions } from "../../../contexts";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import GenericError from "../../../components/GenericError/GenericError";
import { selectors } from "../../../store/state-selectors";
import { useIoContext } from "../../../contexts";
import { HistoricAgreementStatsModel } from "../../../models";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from "../charts/commonChartProps";
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import styles from "./HistoricAgreementStatsWidget.module.scss";
import colors from 'seagull/foundations/colors.module.scss';
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import { format, isSameDay, startOfMonth } from 'date-fns';
import { utils } from "../../../utils";
import { toDatestampObject } from "../../../models/time";
import ConditionalWrapper from "../../../components/ConditionalWrapper/ConditionalWrapper";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import { routes } from "../../../constants";
import { getXAxisTextForQuarterlyDate } from "../charts/charts.utils";
import { getLastXMonths } from "../../../utils/date.utils";
import ThreeLineTopCard from "../GraphWidget/ThreeLineTopCard";
import { AgreementStatus } from "../../../models/relationship-summary";
import { RelationshipProgressStatus } from "../../../models/relationship";
const agreementsColor = colors.blue700;
const proposalsColor = colors.blue500;
const stackId = 'a';
const legendItems = [{
  label: 'Active agreements',
  color: agreementsColor
}, {
  label: 'Proposals awaiting approval',
  color: proposalsColor
}];
const card1Props = {
  testid: 'agreements-summary',
  title: 'Active agreements',
  success: true,
  titleInfo: 'The number of signed agreements that are neither terminated nor completed.',
  secondaryInfo: 'The monthly recurring revenue accounts only for fixed-price services that are automatically billed.'
};
const card2Props = {
  testid: 'proposals-summary',
  title: 'Awaiting approval',
  titleInfo: 'The number of proposals that are awaiting your clients approval.',
  secondaryInfo: 'The monthly recurring revenue accounts only for fixed-price services that are automatically billed. If packages are offered, the package with the lowest value is used in the calculation and optional services, if offered, are excluded from the calculation.'
};
export function fillLast12Months(data, now) {
  return getLastXMonths(now, 12).map(date => data.find(item => isSameDay(startOfMonth(date), startOfMonth(item.date()))) || HistoricAgreementStatsModel.makeEmpty({
    date
  }));
}
function getTooltipItems(payload) {
  if (!payload || !payload.length) return;
  const historicAgreementStatsModel = payload[0].payload;
  return [{
    label: "".concat(utils.formatNumberWithCommas(historicAgreementStatsModel.proposalsCount()), " Proposals awaiting approval"),
    color: proposalsColor
  }, {
    label: "".concat(utils.formatNumberWithCommas(historicAgreementStatsModel.agreementsCount()), " Active agreements"),
    color: agreementsColor
  }];
}
function getTitle(label, payload) {
  if (!payload || !payload.length) return '';
  const historicAgreementStatsModel = payload[0].payload;
  return format(historicAgreementStatsModel.date(), 'MMMM');
}
function HistoricAgreementStatsChart(_ref) {
  let {
    testid,
    data,
    isTestMode
  } = _ref;
  const {
    getNow
  } = useIoContext();
  const barChartProps = isTestMode ? {
    height: 100,
    width: 100
  } : {};
  const fullData = useMemo(() => fillLast12Months(data, getNow()), [data]);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": testid,
    className: styles.chart
  }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: !isTestMode,
    wrapper: children => /*#__PURE__*/React.createElement(ResponsiveContainer, null, children)
  }, /*#__PURE__*/React.createElement(BarChart, _extends({
    data: fullData
  }, barChartProps, {
    barCategoryGap: "10%",
    margin: {
      top: 20,
      left: 16,
      right: 16
    }
  }), /*#__PURE__*/React.createElement(CartesianGrid, commonCartesianGridProps), /*#__PURE__*/React.createElement(Legend, {
    content: /*#__PURE__*/React.createElement(CustomLegend, {
      testid: "".concat(testid, "-legend"),
      items: legendItems
    })
  }), /*#__PURE__*/React.createElement(XAxis, _extends({}, commonXAxisProps, {
    dataKey: item => item.date(),
    height: 49,
    tick: /*#__PURE__*/React.createElement(CustomXAxisTick, {
      tickHeight: 30,
      getText: getXAxisTextForQuarterlyDate
    })
  })), /*#__PURE__*/React.createElement(YAxis, {
    axisLine: false,
    tickLine: false,
    width: 32,
    tickFormatter: value => value === 0 ? '' : utils.formatNumberWithCommas(value),
    fontSize: 12
  }), /*#__PURE__*/React.createElement(Tooltip, {
    cursor: false,
    isAnimationActive: false,
    content: /*#__PURE__*/React.createElement(CustomTooltip, {
      skin: "dark",
      testid: "".concat(testid, "-tooltip"),
      getTitle: getTitle,
      getItems: getTooltipItems
    })
  }), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: item => item.agreementsCount(),
    stackId: stackId,
    fill: agreementsColor,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      getTestId: item => "agreements-for-".concat(toDatestampObject(item.date()).month)
    })
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: item => item.proposalsCount(),
    stackId: stackId,
    fill: proposalsColor,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      hasRadius: true,
      getTestId: item => "proposals-for-".concat(toDatestampObject(item.date()).month)
    })
  })))));
}
function PlaceholderChart(props) {
  const {
    getNow
  } = useIoContext();
  const placeholderData = useMemo(() => {
    const emptyData = fillLast12Months([], getNow());
    return emptyData.map((item, idx) => item.setActiveAgreementsCount(Math.floor(idx / 2) + 3).setProposalsCount(3));
  }, []);
  return /*#__PURE__*/React.createElement(HistoricAgreementStatsChart, _extends({
    testid: "agreement-stats-chart-placeholder",
    data: placeholderData
  }, props));
}
const Layout = _ref2 => {
  let {
    loading,
    children
  } = _ref2;
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    title: "Agreements",
    loading: loading,
    bottomHeight: "l"
  }, children);
};
export default function HistoricAgreementStatsWidget(props) {
  const {
    fetchHistoricAgreementStats
  } = useStoreActions();
  const initialData = useStore(state => state.historicAgreementStats.data);
  const hasSentAProposal = useStore(selectors.hasSentAProposal);
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const loadingOrEmptyBg = () => /*#__PURE__*/React.createElement(PlaceholderChart, props);
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: fetchHistoricAgreementStats,
    initialData: initialData,
    renderChild: _ref3 => {
      let {
        data,
        loading
      } = _ref3;
      const isEmpty = data.length === 0;
      const firstItem = isEmpty ? HistoricAgreementStatsModel.makeEmpty() : data[0];
      return /*#__PURE__*/React.createElement(Layout, {
        loading: loading
      }, /*#__PURE__*/React.createElement(ThreeLineTopCard, _extends({}, card1Props, {
        primaryText: firstItem.agreementsCount(),
        secondaryText: isMemberUser ? undefined : "".concat(firstItem.agreementsDollarAmount(), " monthly recurring"),
        linkTo: "".concat(routes.agreementsDashboard, "?agreementStatus=").concat(AgreementStatus.Active)
      })), /*#__PURE__*/React.createElement(ThreeLineTopCard, _extends({}, card2Props, {
        primaryText: firstItem.proposalsCount(),
        secondaryText: isMemberUser ? undefined : "".concat(firstItem.proposalsDollarAmount(), " monthly recurring"),
        linkTo: "".concat(routes.proposalsDashboard, "?filter=").concat(RelationshipProgressStatus.PaymentMethod, ",").concat(RelationshipProgressStatus.Reviewed, ",").concat(RelationshipProgressStatus.Sent)
      })), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
        empty: isEmpty && !hasSentAProposal,
        emptyMsg: "Here you will view your agreements pipeline over time",
        "data-testid": "agreement-stats",
        loadingOrEmptyBg: loadingOrEmptyBg
      }, /*#__PURE__*/React.createElement(HistoricAgreementStatsChart, _extends({
        testid: "agreement-stats-chart",
        data: data
      }, props))));
    },
    renderLoader: () => /*#__PURE__*/React.createElement(Layout, {
      loading: true
    }, /*#__PURE__*/React.createElement(ThreeLineTopCard, _extends({}, card1Props, {
      loading: true
    })), /*#__PURE__*/React.createElement(ThreeLineTopCard, _extends({}, card2Props, {
      loading: true
    })), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
      loading: true,
      loadingMsg: "Loading data...",
      loadingOrEmptyBg: loadingOrEmptyBg
    })),
    renderError: () => /*#__PURE__*/React.createElement(GenericError, {
      testid: "widget-error"
    })
  });
}